import React, { createContext, useContext, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { authService } from '../services/authService';

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const loadStoredAuth = async () => {
      try {
        // Verificar localStorage primeiro (persistente)
        let storedUser = localStorage.getItem('user');
        let storage = 'localStorage';
        
        // Se não encontrou no localStorage, verificar sessionStorage
        if (!storedUser) {
          storedUser = sessionStorage.getItem('user');
          storage = 'sessionStorage';
        }
        
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          if (userData?.token) {
            // Verificar se a sessão ainda é válida
            const sessionValid = authService.isSessionValid ? authService.isSessionValid(userData) : true;
            
            if (sessionValid) {
              setUser(userData);
            } else {
              localStorage.removeItem('user');
              sessionStorage.removeItem('user');
              setUser(null);
            }
          } else {
            localStorage.removeItem('user');
            sessionStorage.removeItem('user');
            setUser(null);
          }
        } else {
          setUser(null);
        }
      } catch (error) {
        localStorage.removeItem('user');
        sessionStorage.removeItem('user');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    loadStoredAuth();
  }, []);

  const signIn = async (sessionData) => {
    try {
      let token = null;
      let userId = null;
      
      // Para login com Google ou Firebase
      if (sessionData.token) {
        token = sessionData.token;
        userId = sessionData.id;
      } 
      // Fallback para stsTokenManager do Google
      else if (sessionData.stsTokenManager?.accessToken) {
        token = sessionData.stsTokenManager.accessToken;
        userId = sessionData.uid;
      }

      if (!token || !userId) {
        throw new Error('Dados de autenticação inválidos');
      }

      const userData = {
        ...sessionData,
        id: userId,
        token
      };

      // Sempre configurar o token no axios
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
      
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      
      return userData;
    } catch (error) {
      console.error('[AuthContext] Erro no signIn:', error);
      throw error;
    }
  };

  const signOut = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('@MeuEsporte:refreshToken');
  };

  const handleSessionExpired = () => {
    // Verificar se já está na página de login antes de redirecionar
    if (window.location.pathname !== '/login') {
      signOut();
      // Limpar quaisquer dados de redirecionamento pendentes
      localStorage.removeItem('redirectData');
      sessionStorage.removeItem('redirectData');
      window.location.replace('/login?expired=true');
    }
  };

  const verifyToken = async () => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) return false;

    try {
      const userData = JSON.parse(storedUser);
      const token = userData?.token;
      
      if (!token) {
        handleSessionExpired();
        return false;
      }

      const isValid = await authService.verifyToken(token);
      if (!isValid) {
        handleSessionExpired();
        return false;
      }

      // Atualizar headers do axios após verificação
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
      return true;
    } catch (error) {
      console.error('Erro ao verificar token:', error);
      handleSessionExpired();
      return false;
    }
  };

  useEffect(() => {
    const checkSession = () => {
      if (user && !verifyToken()) {
        signOut();
        window.location.href = '/login';
      }
    };

    // Estado de atividade do usuário
    let isUserActive = true;
    let lastActivity = Date.now();

    // Verificar sessão com intervalos adaptativos
    const getCheckInterval = () => {
      const timeSinceLastActivity = Date.now() - lastActivity;
      const INACTIVITY_THRESHOLD = 10 * 60 * 1000; // 10 minutos
      
      // Se usuário inativo por mais de 10 min, verificar menos frequentemente
      return timeSinceLastActivity > INACTIVITY_THRESHOLD 
        ? 30 * 60 * 1000  // 30 minutos para usuário inativo
        : 15 * 60 * 1000; // 15 minutos para usuário ativo
    };

    const scheduleNextCheck = () => {
      const interval = getCheckInterval();
      return setTimeout(checkSession, interval);
    };

    // Iniciar verificação adaptativa
    let sessionCheckTimeout = scheduleNextCheck();
    
    // Verificar quando a janela recebe foco (usuário voltou)
    const handleFocus = () => {
      if (user) {
        clearTimeout(sessionCheckTimeout);
        checkSession();
        sessionCheckTimeout = scheduleNextCheck();
      }
    };
    
    // Verificar quando há atividade do usuário
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    let activityTimeout;
    
    const handleActivity = () => {
      clearTimeout(activityTimeout);
      activityTimeout = setTimeout(() => {
        lastActivity = Date.now();
        isUserActive = true;
        
        if (user) {
          authService.updateLastActivity();
          
          // Reagendar próxima verificação com novo intervalo
          clearTimeout(sessionCheckTimeout);
          sessionCheckTimeout = scheduleNextCheck();
        }
      }, 1000); // Debounce de 1 segundo
    };
    
    window.addEventListener('focus', handleFocus);
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, true);
    });

    return () => {
      clearTimeout(sessionCheckTimeout);
      clearTimeout(activityTimeout);
      window.removeEventListener('focus', handleFocus);
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity, true);
      });
    };
  }, [user]);

  // Verifica se a rota atual é uma rota pública ou de convite
  const isPublicRoute = ['/login', '/', '/register', '/termos-de-uso', '/demo'].includes(location.pathname);
  const isInviteRoute = location.pathname.includes('/convite/') || location.pathname.includes('/grupo/') && location.pathname.includes('/convite');

  return (
    <AuthContext.Provider value={{ 
      user, 
      signed: !!user, 
      signIn, 
      signOut,
      handleSessionExpired,
      loading 
    }}>
      {!loading && (
        !user && !isPublicRoute && !isInviteRoute ? (
          <Navigate to="/login" replace />
        ) : (
          children
        )
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  return context;
}
